(() => {
  // ========== 設定 ==========
  const USE_IO = true; // 画面入り直前で初期化。即初期化試験は false
  const CDN_FALLBACK = "https://unpkg.com/swiper/swiper-bundle.min.js";
  let loaded = null; // Swiper本体ロードのPromiseキャッシュ

  // ========== Swiperを必要時だけロード ==========
  function ensureSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (loaded) return loaded;
    loaded = new Promise((res) => {
      const src = window.SWIPER_JS_SRC || CDN_FALLBACK;
      const s = document.createElement("script");
      s.src = src;
      s.defer = true;
      s.onload = res;
      s.onerror = () => {
        // フォールバック（ローカル失敗→CDN）
        if (src !== CDN_FALLBACK) {
          const f = document.createElement("script");
          f.src = CDN_FALLBACK;
          f.defer = true;
          f.onload = res;
          document.head.appendChild(f);
        }
      };
      document.head.appendChild(s);
    });
    return loaded;
  }

  // ========== オプション/後描画待ち ==========
  function parseOptions(el) {
    try {
      return JSON.parse(el.getAttribute("data-options") || "{}");
    } catch {
      return {};
    }
  }
  function whenSlidesReady(root, cb) {
    // 動的差し替えにも耐えるよう、root配下全体を監視
    const hasSlides = () => {
      const wrap = root.querySelector(".swiper-wrapper");
      return wrap && !!wrap.querySelector(".swiper-slide");
    };
    if (hasSlides()) return cb();
    const mo = new MutationObserver(() => {
      if (hasSlides()) {
        mo.disconnect();
        cb();
      }
    });
    mo.observe(root, { childList: true, subtree: true });
  }

  // ========== 単一セクション初期化 ==========
  function initOne(root) {
    if (!root || root.dataset.inited) return;

    const opts = parseOptions(root);
    // breakpoints のキーを文字列から数値に変換（Swiper は数値キーを期待）
    const normalizedBreakpoints = opts.breakpoints
      ? Object.keys(opts.breakpoints).reduce((acc, key) => {
          const numKey = parseInt(key, 10);
          if (!isNaN(numKey)) {
            acc[numKey] = opts.breakpoints[key];
          }
          return acc;
        }, {})
      : {};

    const base = {
      slidesPerView: opts.slidesPerView ?? 1,
      spaceBetween: opts.spaceBetween ?? 12,
      loop: !!opts.loop,
      pagination: {
        el: root.querySelector(".swiper-pagination"),
        clickable: true,
      },
      navigation: {
        nextEl: root.querySelector(".swiper-button-next"),
        prevEl: root.querySelector(".swiper-button-prev"),
      },
      breakpoints:
        Object.keys(normalizedBreakpoints).length > 0
          ? normalizedBreakpoints
          : undefined,
    };
    if (opts.autoplay)
      base.autoplay = { delay: 3500, disableOnInteraction: false };
    if (opts.speed) base.speed = opts.speed;
    if (opts.effect) {
      base.effect = opts.effect;
      if (opts.effect === "fade")
        base.fadeEffect = { crossFade: !!opts.crossFade };
    }
    // scrollbar オプション（要素が存在する場合のみ有効化）
    const scrollbarEl = root.querySelector(".swiper-scrollbar");
    if (scrollbarEl) {
      base.scrollbar = {
        el: scrollbarEl,
        draggable: opts.scrollbar?.draggable !== false,
        hide: opts.scrollbar?.hide || false,
        ...(opts.scrollbar || {}),
      };
    }

    const create = () => {
      // 直前に最新の要素を取得（差し替え対策）
      const swiperEl = root.querySelector(".swiper");
      const wrapper = root.querySelector(".swiper-wrapper");
      if (!swiperEl || !wrapper) return;
      // レイアウト確定の次フレームで初期化
      requestAnimationFrame(() => {
        const inst = new Swiper(swiperEl, base);

        // 幅0→表示になったケースに追従
        const io = new IntersectionObserver(([e], o) => {
          if (e.isIntersecting) {
            inst.update();
            o.disconnect();
          }
        });
        io.observe(swiperEl);

        root.dataset.inited = "1";
      });
    };

    // すでにスライドあり → すぐ初期化、なければ出現を待つ
    const initialWrapper = root.querySelector(".swiper-wrapper");
    if (initialWrapper && initialWrapper.querySelector(".swiper-slide")) {
      create();
    } else {
      whenSlidesReady(root, create);
    }
  }

  // ========== 画面内なら即初期化（IOの取りこぼし防止） ==========
  function isInViewport(el) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return r.bottom >= 0 && r.right >= 0 && r.top <= vh && r.left <= vw;
  }

  // ========== 全セクションを起動 ==========
  function bootAll() {
    const targets = Array.from(
      document.querySelectorAll(".js-swiper-section:not([data-inited])")
    );
    if (!targets.length) return;

    ensureSwiper().then(() => {
      if (!USE_IO) {
        targets.forEach(initOne);
        return;
      }
      // すでに画面内のものは即初期化（フルリロード時の抜け漏れ対策）
      const later = [];
      for (const t of targets) isInViewport(t) ? initOne(t) : later.push(t);

      // 残りはIOで監視
      if (later.length) {
        const io = new IntersectionObserver(
          (ents, o) => {
            for (const e of ents) {
              if (!e.isIntersecting) continue;
              o.unobserve(e.target);
              initOne(e.target);
            }
          },
          { rootMargin: "200px" }
        );
        later.forEach((t) => io.observe(t));
      }
    });
  }

  // ========== とにかく多タイミングで起動（フルリロード対策） ==========
  bootAll(); // 即
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll, { once: true });
  }
  window.addEventListener("load", bootAll, { once: true }); // リソース完了後
  document.addEventListener("shopify:section:load", bootAll); // Theme Editor
  document.addEventListener("page:loaded", bootAll); // 一部テーマのカスタムイベント
  document.addEventListener("route:change", bootAll); // PJAX/スムーズ遷移系
  document.addEventListener("recently-viewed:render", bootAll); // 互換: kebab-case
  document.addEventListener("recentlyViewed:loaded", bootAll); // テーマ実装のイベント名
  // eventBus がある場合はそれもフック
  if (window.eventBus && typeof window.eventBus.on === "function") {
    try {
      window.eventBus.on("recentlyViewed:updated", bootAll);
      window.eventBus.on("recentlyViewed:loaded", bootAll);
    } catch (_) {}
  } else {
    // DOM カスタムイベント経由でも捕捉
    document.addEventListener("recentlyViewed:updated", bootAll);
  }

  // フルリロード時の遅延描画に備えたリトライ（軽量）
  [500, 1500, 3000].forEach((ms) => setTimeout(bootAll, ms));

  // タブ復帰などで初期化漏れがあれば再走
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") bootAll();
  });
})();
